import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
    ArrowLeft, Send, Info, Inbox, Radio, Users, Clock, Loader2,
    Image as ImageIcon, Mic, Flag, LogOut, X, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { ParallaxBackground } from './utils/parallax';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { FormattedText } from './FormattedText';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { StorageService } from '../services/StorageService';
import { RoomService, lifecycleStateOf, toMillis } from '../services/RoomService';
import {
    ROOM_MAX_MEMBERS,
    ROOM_MIN_MEMBERS,
    formatTimeRemaining,
    isMuted,
    ROOM_GRACE_PERIOD_MINUTES,
    normalizeJoinPolicy,
    ROOM_ANSWER_MAX_LENGTH,
} from '../services/roomLifecycle';
import { formatTimeAgo } from './utils/timeFormat';
import type { Room, RoomMember } from '../types';
import { RoomJoinRequestsModal } from './RoomJoinRequestsModal';
import { RoomInfoModal } from './RoomInfoModal';
import { InviteToRoomModal } from './InviteToRoomModal';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { useImageViewer } from './ui/ImageViewerProvider';

interface RoomScreenProps {
    roomId: string;
    onBack: () => void;
}

/**
 * A live room: chat, with a countdown that never leaves the header.
 *
 * Structurally this follows TribeGroupScreen — a group screen that knows who you
 * are inside it — but the body is a chat rather than a post feed, so the message
 * documents, the composer, the voice recorder and the media uploads are the ones
 * DirectMessageScreen already uses.
 *
 * Non-members get the lobby view instead: what the room is, and a request to
 * join. There is no auto-join anywhere.
 */
/**
 * Tell the server which conversation is on screen, so it does not push about it.
 *
 * The in-app toast can be suppressed on the client, but a NATIVE push is drawn
 * by the OS and cannot be taken back once sent — so the send itself has to be
 * skipped, which means the server has to know. onNotificationCreated reads this.
 *
 * Cleared on unmount AND on tab blur: a stale value costs at most one missed
 * notification, and backgrounding the app is exactly when you want them again.
 */
function useActiveConversation(key: string | null) {
    useEffect(() => {
        let cancelled = false;
        const write = async (value: string | null) => {
            try {
                const { getDbSafe, getAuthSafe } = await import('../services/firebaseConfig');
                const { doc, updateDoc } = await import('firebase/firestore');
                const auth = await getAuthSafe();
                const uid = auth.currentUser?.uid;
                if (!uid || cancelled) return;
                const db = await getDbSafe();
                await updateDoc(doc(db, 'users', uid), { activeConversation: value || '' });
            } catch { /* presence is an optimisation, never a blocker */ }
        };

        if (key) write(key);
        const onHide = () => { if (document.visibilityState === 'hidden') write(null); };
        const onShow = () => { if (document.visibilityState === 'visible' && key) write(key); };
        document.addEventListener('visibilitychange', onHide);
        document.addEventListener('visibilitychange', onShow);

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onHide);
            document.removeEventListener('visibilitychange', onShow);
            write(null);
        };
    }, [key]);
}

export function RoomScreen({ roomId, onBack }: RoomScreenProps) {
    const { userProfile, firebaseUser } = useUserData();
    const { isHiddenContent } = useContentFilter();

    const [room, setRoom] = useState<Room | null>(null);
    const [membership, setMembership] = useState<RoomMember | null>(null);
    const [messages, setMessages] = useState<any[]>([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(true);
    useScreenReady(!loading);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [requestState, setRequestState] = useState<'none' | 'pending' | 'denied'>('none');
    const [joinAnswer, setJoinAnswer] = useState('');
    const [showInfo, setShowInfo] = useState(false);
    const [showRequests, setShowRequests] = useState(false);
    const [showInvite, setShowInvite] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const fileRef = useRef<HTMLInputElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const { open: openImage } = useImageViewer();

    const {
        isRecording, isPaused, duration, startRecording, stopRecording,
        pauseRecording, resumeRecording, audioBlob, reset: resetRecorder,
    } = useVoiceRecorder(60000);

    const myId = String(firebaseUser?.uid || userProfile?.id || '');
    const isOwner = !!room && String(room.ownerId) === myId;
    const isMod = !!room && Array.isArray(room.moderators) && room.moderators.includes(myId);
    const isStaff = isOwner || isMod;
    const isActiveMember = membership?.status === 'active';
    useActiveConversation(isActiveMember ? `room:${roomId}` : null);
    const muted = isMuted({ mutedUntil: toMillis(membership?.mutedUntil) }, now);

    const state = useMemo(() => (room ? lifecycleStateOf(room) : null), [room]);
    const joinPolicy = normalizeJoinPolicy((room as any)?.joinPolicy);
    const joinQuestion = String((room as any)?.joinQuestion || '');

    // ── Subscriptions ───────────────────────────────────────────────────────
    useEffect(() => RoomService.subscribeToRoom(roomId, (next) => {
        setRoom(next);
        setLoading(false);
    }), [roomId]);

    useEffect(() => {
        if (!myId) return;
        let cancelled = false;
        (async () => {
            const [mine, request] = await Promise.all([
                RoomService.getMyMembership(roomId, myId),
                RoomService.getMyJoinRequest(roomId, myId),
            ]);
            if (cancelled) return;
            setMembership(mine);
            setRequestState(
                request?.status === 'pending' ? 'pending'
                    : request?.status === 'denied' ? 'denied'
                        : 'none',
            );
        })();
        return () => { cancelled = true; };
        // room?.members is in the deps so an approval elsewhere refreshes this view.
    }, [roomId, myId, room?.memberCount]);

    // Messages are members-only, both here and in the security rules, so this
    // subscription is only opened once membership is confirmed.
    useEffect(() => {
        if (!isActiveMember) { setMessages([]); return; }
        return RoomService.subscribeToRoomMessages(roomId, setMessages);
    }, [roomId, isActiveMember]);

    useEffect(() => {
        if (!isStaff) { setPendingCount(0); return; }
        return RoomService.subscribeToRoomJoinRequests(roomId, (reqs) => setPendingCount(reqs.length));
    }, [roomId, isStaff]);

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    // Arriving from a "someone wants to join" notification should land on the
    // approval list, not just inside the room. Tapping a notification about a
    // decision you need to make and being dropped somewhere you still have to
    // navigate from is the notification only half working.
    //
    // Waits for staff status: the modal is owner/mod only, and membership
    // resolves a moment after the room does.
    useEffect(() => {
        if (!isStaff) return;
        try {
            const flag = sessionStorage.getItem('open_room_requests_for');
            if (flag && flag === roomId) {
                setShowRequests(true);
                sessionStorage.removeItem('open_room_requests_for');
            }
        } catch { /* storage unavailable */ }
    }, [isStaff, roomId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    // A room that ends while someone is standing in it should say so rather than
    // silently emptying out.
    useEffect(() => {
        if (!loading && room === null) {
            toast.info('This room has ended.');
            onBack();
        }
    }, [loading, room, onBack]);

    const visibleMessages = useMemo(
        () => messages.filter(m => !isHiddenContent(m)),
        [messages, isHiddenContent],
    );

    /**
     * Open the profile of whoever sent a message.
     *
     * Tapping a face and having nothing happen is the kind of dead end that
     * makes a screen feel unfinished — every other surface in the app opens a
     * profile from an avatar. Uses the same event AppRouter already handles, and
     * that handler redirects to your own profile when the id is yours.
     */
    const openSenderProfile = (m: any) => {
        const senderId = String(m?.senderId || '');
        if (!senderId) return;
        window.dispatchEvent(new CustomEvent('navigateToFriendProfile', {
            detail: {
                id: senderId,
                name: m.senderName || '',
                username: String(m.senderUsername || '').replace(/^@/, ''),
                image: m.senderAvatar || '',
            },
        }));
    };

    // ── Actions ─────────────────────────────────────────────────────────────
    const send = async (extra?: any, body = '') => {
        const payload = (body || text).trim();
        if (!payload && !extra) return;
        setSending(true);
        try {
            await RoomService.sendRoomMessage(roomId, myId, payload, extra);
            setText('');
        } catch (e: any) {
            toast.error(e?.message || 'Could not send that.');
        } finally {
            setSending(false);
        }
    };

    const handleAttach = async (file: File) => {
        if (!firebaseUser) return;
        const isVideo = file.type.startsWith('video/');
        const loading = toast.loading(isVideo ? 'Uploading video...' : 'Uploading image...');
        try {
            const url = isVideo
                ? await StorageService.uploadYapVideo(firebaseUser.uid, file)
                : await StorageService.uploadChatImage(firebaseUser.uid, file);
            await send(isVideo ? { videoUrl: url } : { imageUrl: url });
            toast.dismiss(loading);
        } catch (e: any) {
            toast.dismiss(loading);
            toast.error(e?.message || 'Upload failed.');
        }
    };

    useEffect(() => {
        if (!audioBlob || !firebaseUser) return;
        (async () => {
            const loading = toast.loading('Sending voice note...');
            try {
                const audioUrl = await StorageService.uploadVoiceReply(firebaseUser.uid, audioBlob);
                await send({ audioUrl, audioDuration: duration });
                toast.dismiss(loading);
            } catch (e: any) {
                toast.dismiss(loading);
                toast.error(e?.message || 'Could not send the voice note.');
            } finally {
                resetRecorder();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioBlob]);

    const handleRequestJoin = async () => {
        setRequesting(true);
        try {
            await RoomService.createRoomJoinRequest(myId, roomId, joinAnswer);
            // An open room admits on the spot, so calling that a "request" that
            // someone will review would be wrong. The membership itself is minted
            // by the server trigger either way.
            if (joinPolicy === 'open') {
                toast.success("You're in");
            } else {
                setRequestState('pending');
                toast.success('Request sent', { description: 'The host or a room mod will let you in.' });
            }
            setJoinAnswer('');
        } catch (e: any) {
            toast.error(e?.message || 'Could not send the request.');
        } finally {
            setRequesting(false);
        }
    };

    const handleLeave = async () => {
        try {
            const { closed } = await RoomService.leaveRoom(myId, roomId);
            toast.success(closed ? 'Room ended' : 'You left the room');
            onBack();
        } catch (e: any) {
            toast.error(e?.message || 'Could not leave.');
        }
    };

    const handleReport = async (messageId: string) => {
        const reason = window.prompt('What is wrong with this message?');
        if (!reason || reason.trim().length < 4) {
            if (reason !== null) toast.error('Give a short reason so a moderator can act.');
            return;
        }
        try {
            await RoomService.reportRoomMessage(roomId, messageId, myId, reason.trim());
            toast.success('Reported', { description: 'A moderator will review it.' });
        } catch (e: any) {
            toast.error(e?.message || 'Could not report that.');
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="w-full h-screen flex items-center justify-center bg-light-pattern dark:bg-[#0D0D0D]">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}><Loader2 className="w-8 h-8" style={{ color: '#4478ea' }} /></motion.div>
            </div>
        );
    }

    if (!room || !state) return null;

    const waiting = room.status === 'lobby';
    const countdown = waiting
        ? `Waiting for ${Math.max(0, ROOM_MIN_MEMBERS - state.memberCount)} more`
        : formatTimeRemaining(state.expiresAt, now);

    return (
        <div className="w-full h-screen relative bg-light-pattern dark:bg-[#0D0D0D]">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <ParallaxBackground speed={0.05} />
            </div>

            <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
                {/* Header — the countdown lives here and never scrolls away. */}
                <div
                    className="px-4 pb-3 border-b border-black/10 dark:border-white/10 bg-[#F5F5DC]/80 dark:bg-[#121212] backdrop-blur-sm"
                    style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
                >
                    <div className="flex items-center gap-3">
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={onBack}
                            className="text-black dark:text-[#E5E5E5] shrink-0"
                            aria-label="Back"
                        >
                            <ArrowLeft className="w-6 h-6" />
                        </motion.button>

                        <div className="min-w-0 flex-1">
                            <p className="text-black dark:text-white truncate" style={{ fontWeight: 700, fontSize: 17 }}>
                                {room.title}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-[#666666] dark:text-[#a0a0a0]">
                                <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {state.memberCount}/{ROOM_MAX_MEMBERS}
                                </span>
                                <span
                                    className="flex items-center gap-1"
                                    style={{
                                        color: room.status === 'grace' ? '#E74C3C' : undefined,
                                        fontWeight: 700,
                                    }}
                                >
                                    <Clock className="w-3 h-3" />
                                    {countdown}
                                </span>
                            </div>
                        </div>

                        {isStaff && (
                            <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setShowRequests(true)}
                                className="relative w-10 h-10 rounded-xl flex items-center justify-center bg-black/5 dark:bg-white/5 shrink-0"
                                aria-label="Join requests"
                            >
                                <Inbox className="w-5 h-5 text-black dark:text-white" />
                                {pendingCount > 0 && (
                                    <span
                                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white"
                                        style={{ background: '#E74C3C', fontSize: 10, fontWeight: 700 }}
                                    >
                                        {pendingCount > 9 ? '9+' : pendingCount}
                                    </span>
                                )}
                            </motion.button>
                        )}

                        {isActiveMember && state.memberCount < ROOM_MAX_MEMBERS && (
                            <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setShowInvite(true)}
                                className="w-10 h-10 rounded-xl flex items-center justify-center bg-black/5 dark:bg-white/5 shrink-0"
                                aria-label="Invite friends"
                            >
                                <UserPlus className="w-5 h-5 text-black dark:text-white" />
                            </motion.button>
                        )}

                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowInfo(true)}
                            className="w-10 h-10 rounded-xl flex items-center justify-center bg-black/5 dark:bg-white/5 shrink-0"
                            aria-label="Room info"
                        >
                            <Info className="w-5 h-5 text-black dark:text-white" />
                        </motion.button>
                    </div>

                    {room.status === 'grace' && (
                        <div
                            className="mt-2 px-3 py-2 rounded-xl"
                            style={{ background: 'rgba(231,76,60,0.12)' }}
                        >
                            <p style={{ color: '#E74C3C', fontSize: 12, fontWeight: 700 }}>
                                Below {ROOM_MIN_MEMBERS} members. The clock is paused: this room closes in {ROOM_GRACE_PERIOD_MINUTES / 60} hours unless someone joins.
                            </p>
                        </div>
                    )}
                </div>

                {/* Body */}
                {!isActiveMember ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center" style={{ paddingLeft: 32, paddingRight: 32 }}>
                        <div
                            className="w-20 h-20 rounded-full flex items-center justify-center"
                            style={{ background: 'rgba(68,120,234,0.15)', marginBottom: 20 }}
                        >
                            <Radio className="w-9 h-9" style={{ color: '#4478ea' }} />
                        </div>
                        <h2 className="text-xl text-black dark:text-white mb-2" style={{ fontWeight: 700 }}>
                            {room.title}
                        </h2>
                        <p className="text-[#666666] dark:text-[#a0a0a0] max-w-sm mb-6" style={{ fontSize: 14 }}>
                            {membership?.status === 'kicked'
                                ? 'You were removed from this room.'
                                : requestState === 'pending'
                                    ? 'Your request is waiting for the host or a room mod.'
                                    : joinPolicy === 'open'
                                        ? 'This room is open — join and leave whenever you like.'
                                        : joinPolicy === 'question'
                                            ? 'The host asks everyone one question before letting them in.'
                                            : 'Ask to join and the host or a room mod will let you in.'}
                        </p>

                        {/* The host's question, and the answer that goes with the
                            request. Nothing is checked against a stored answer —
                            whoever approves reads it and decides. */}
                        {joinPolicy === 'question'
                          && membership?.status !== 'kicked'
                          && requestState !== 'pending'
                          && joinQuestion && (
                            <div className="w-full max-w-sm mb-4 text-left">
                                <p className="text-black dark:text-white" style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                                    {joinQuestion}
                                </p>
                                <textarea
                                    value={joinAnswer}
                                    onChange={(e) => setJoinAnswer(e.target.value.slice(0, ROOM_ANSWER_MAX_LENGTH))}
                                    rows={3}
                                    placeholder="Your answer"
                                    className="w-full rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white px-3 py-2 outline-none resize-none"
                                    style={{ fontSize: 13 }}
                                />
                                <p className="text-[#666666] dark:text-[#a0a0a0] text-right mt-1" style={{ fontSize: 10 }}>
                                    {joinAnswer.length}/{ROOM_ANSWER_MAX_LENGTH}
                                </p>
                            </div>
                        )}

                        {membership?.status !== 'kicked' && requestState !== 'pending' && (
                            <Button
                                onClick={handleRequestJoin}
                                disabled={requesting}
                                className="px-6 h-11 rounded-2xl bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-lg"
                                style={{ fontWeight: 700 }}
                            >
                                {requesting ? (joinPolicy === 'open' ? 'Joining...' : 'Sending...') : (joinPolicy === 'open' ? 'Join room' : 'Request to join')}
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
                            <div className="p-4 space-y-3">
                                {visibleMessages.length === 0 && (
                                    <p
                                        className="text-center text-[#666666] dark:text-[#a0a0a0]"
                                        style={{ fontSize: 13, paddingTop: 32 }}
                                    >
                                        {waiting
                                            ? `The clock starts once ${ROOM_MIN_MEMBERS} people are here.`
                                            : 'Say something.'}
                                    </p>
                                )}

                                {visibleMessages.map((m) => {
                                    const mine = String(m.senderId) === myId;

                                    // Joins and leaves are events, not conversation:
                                    // centred, no bubble, no avatar, so the eye skips
                                    // them while scanning what people actually said.
                                    if (m.type === 'system') {
                                        return (
                                            <p
                                                key={m.id}
                                                className="text-center text-[#666666] dark:text-[#a0a0a0]"
                                                style={{ fontSize: 11, fontWeight: 600 }}
                                            >
                                                {m.text}
                                            </p>
                                        );
                                    }

                                    return (
                                        <div
                                            key={m.id}
                                            className="flex gap-2"
                                            // Inline, because flex-row-reverse and max-w-[75%]
                                            // have no rules in the committed pre-compiled
                                            // index.css — which is why your own messages never
                                            // moved to the right and bubbles had no width cap.
                                            style={{ flexDirection: mine ? 'row-reverse' : 'row' }}
                                        >
                                            <Avatar
                                                className="w-8 h-8 shrink-0 cursor-pointer"
                                                onClick={() => openSenderProfile(m)}
                                            >
                                                <AvatarImage src={m.senderAvatar || ''} />
                                                <AvatarFallback>{(m.senderName || '?')[0]}</AvatarFallback>
                                            </Avatar>
                                            <div
                                                className="min-w-0 flex flex-col"
                                                style={{ maxWidth: '75%', alignItems: mine ? 'flex-end' : 'flex-start' }}
                                            >
                                                <div
                                                    className="flex items-center gap-2"
                                                    style={{ flexDirection: mine ? 'row-reverse' : 'row' }}
                                                >
                                                    <span
                                                        className="text-[#666666] dark:text-[#a0a0a0] truncate"
                                                        style={{ fontSize: 11, fontWeight: 700 }}
                                                    >
                                                        {mine ? 'You' : (m.senderName || 'Someone')}
                                                    </span>
                                                    {!mine && (
                                                        <button
                                                            onClick={() => handleReport(m.id)}
                                                            className="text-[#666666] dark:text-[#a0a0a0]"
                                                            aria-label="Report message"
                                                        >
                                                            <Flag className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div
                                                    className="px-3 py-2 rounded-2xl mt-1"
                                                    style={{
                                                        background: mine
                                                            ? 'rgba(68,120,234,0.18)'
                                                            : 'rgba(127,127,127,0.14)',
                                                    }}
                                                >
                                                    {m.text && (
                                                        <FormattedText
                                                            text={m.text}
                                                            className="text-black dark:text-white whitespace-pre-wrap block"
                                                        />
                                                    )}
                                                    {m.imageUrl && (
                                                        <img
                                                            src={m.imageUrl}
                                                            alt=""
                                                            className="rounded-xl mt-1 cursor-pointer"
                                                            style={{ maxWidth: '100%', display: 'block' }}
                                                            onClick={(e) => { e.stopPropagation(); openImage([m.imageUrl], 0); }}
                                                        />
                                                    )}
                                                    {m.videoUrl && (
                                                        <video
                                                            src={m.videoUrl}
                                                            controls
                                                            playsInline
                                                            className="rounded-xl mt-1"
                                                            style={{ maxWidth: '100%', display: 'block' }}
                                                        />
                                                    )}
                                                    {m.audioUrl && (
                                                        <VoiceReplyBubble
                                                            audioUrl={m.audioUrl}
                                                            duration={m.audioDuration}
                                                            className="w-full"
                                                        />
                                                    )}
                                                </div>
                                                {/* Under the bubble rather than beside the name: the time is
                                                    reference, not identity, and reading it out of the header meant
                                                    scanning past it to reach who was speaking. Sits on the same
                                                    edge as the bubble, so it lands bottom-right on your own
                                                    messages. */}
                                                <span
                                                  className="text-[#666666] dark:text-[#a0a0a0]"
                                                  style={{ fontSize: 10, marginTop: 2 }}
                                                >
                                                  {formatTimeAgo(m.timestamp)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>
                        </ScrollArea>

                        {/* Composer */}
                        <div
                            className="p-3 border-t border-black/10 dark:border-white/10 bg-[#F5F5DC]/80 dark:bg-[#121212]"
                            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
                        >
                            {muted ? (
                                <p
                                    className="text-center py-2"
                                    style={{ color: '#E74C3C', fontSize: 13, fontWeight: 700 }}
                                >
                                    You are muted in this room.
                                </p>
                            ) : isRecording ? (
                                <VoiceRecorderUI
                                    isRecording={isRecording}
                                    isPaused={isPaused}
                                    duration={duration}
                                    maxDuration={60000}
                                    onStart={startRecording}
                                    onStop={stopRecording}
                                    onCancel={resetRecorder}
                                    onPause={pauseRecording}
                                    onResume={resumeRecording}
                                />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*,video/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) void handleAttach(f);
                                            e.target.value = '';
                                        }}
                                    />
                                    <button
                                        onClick={() => fileRef.current?.click()}
                                        className="w-10 h-10 rounded-full flex items-center justify-center bg-black/5 dark:bg-white/5 shrink-0"
                                        aria-label="Attach"
                                    >
                                        <ImageIcon className="w-5 h-5 text-black dark:text-white" />
                                    </button>
                                    <button
                                        onClick={startRecording}
                                        className="w-10 h-10 rounded-full flex items-center justify-center bg-black/5 dark:bg-white/5 shrink-0"
                                        aria-label="Record voice note"
                                    >
                                        <Mic className="w-5 h-5 text-black dark:text-white" />
                                    </button>
                                    <Input
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
                                        placeholder="Message the room..."
                                        className="flex-1 bg-black/5 dark:bg-white/5 border-transparent focus:border-[#F39C12] rounded-2xl"
                                    />
                                    <button
                                        onClick={() => void send()}
                                        disabled={sending || !text.trim()}
                                        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 disabled:opacity-40"
                                        style={{ background: '#4478ea' }}
                                        aria-label="Send"
                                    >
                                        <Send className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <RoomInfoModal
                isOpen={showInfo}
                onClose={() => setShowInfo(false)}
                room={room}
                myId={myId}
                onLeave={handleLeave}
                // The modal has already ended the room and said so; this just
                // takes the host out of it. Sending this through handleLeave
                // called leaveRoom against the closed room and toasted twice.
                onEnded={onBack}
            />
            <InviteToRoomModal
                isOpen={showInvite}
                onClose={() => setShowInvite(false)}
                roomId={roomId}
                roomTitle={room.title}
                inviterId={myId}
                memberIds={Array.isArray(room.members) ? room.members.map(String) : []}
                seatsLeft={Math.max(0, ROOM_MAX_MEMBERS - state.memberCount)}
            />
            <RoomJoinRequestsModal
                isOpen={showRequests}
                onClose={() => setShowRequests(false)}
                roomId={roomId}
                roomTitle={room.title}
                actorId={myId}
            />
        </div>
    );
}

export default RoomScreen;
